import { useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  useTrainingWorksheet,
  useTrainingVocabularies,
  useUpdateTrainingWorksheetRow,
} from '@/hooks/use-api'
import { useUser } from '@/contexts/UserContext'
import { useToast } from '@/hooks/use-toast'
import { useDebounce } from '@/hooks/use-debounce'
import { usePersistentState, makeFilterKey } from '@/hooks/use-persistent-state'
import { explainError } from '@/lib/explain-error'
import { AccountAliasResolver } from '@/components/AccountAliasResolver'
import {
  SheetPage,
  SheetTable,
  SheetColourKey,
  ShowCompletedToggle,
  CompletedHiddenNote,
  NothingOutstanding,
  type SheetColumn,
} from './SheetTable'
import type { TrainingWorksheetKey, TrainingWorksheetRow, TrainingVocabularies } from '@/types'

export interface WorksheetScreenProps {
  sheet: TrainingWorksheetKey
  title: string
  description: string
  icon?: React.ReactNode
  /**
   * Columns, given the server's vocabularies. A function rather than an array
   * because the dropdown options come from `/training/vocabularies` — hardcoding
   * them in the page is how the two drift apart.
   */
  columns: (vocab?: TrainingVocabularies) => SheetColumn[]
  /** Which column drives the row tint, and the legend that explains it. */
  tints?: Record<string, string>
  tintFor?: (row: TrainingWorksheetRow) => string | undefined
  /** How a row names itself in an accessible label. Usually the client. */
  rowLabel: (row: TrainingWorksheetRow) => string
  caption: string
  searchPlaceholder: string
  emptyMessage?: React.ReactNode
}

/**
 * One of the five tracker worksheets, as a page.
 *
 * All five behave identically — a wide editable grid, a search box, the unmatched
 * account queue, a colour key — so this holds that behaviour once and each page
 * supplies only its columns and copy. The alternative was five near-identical
 * 200-line files, which is how the worklist's column set fell out of step with the
 * sheet in the first place.
 *
 * Editing is schedulers-only here, matching PATCH /training/worksheets/:sheet: a
 * Training account can read these tabs but nothing on them is owned by a particular
 * trainer, so there is no per-row ownership to grant them.
 */
export function WorksheetScreen({
  sheet,
  title,
  description,
  icon,
  columns,
  tints,
  tintFor,
  rowLabel,
  caption,
  searchPlaceholder,
  emptyMessage,
}: WorksheetScreenProps) {
  const { currentUser, userType } = useUser()
  const { toast } = useToast()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)

  // One key for the whole section, not one per tab: someone who wants to see finished
  // work wants to see it everywhere, and setting the same toggle five times is a chore.
  const fk = makeFilterKey(currentUser?.id, 'training')
  const [showCompleted, setShowCompleted] = usePersistentState(fk('showCompleted'), false)

  const { data, isLoading } = useTrainingWorksheet(sheet, {
    search: debouncedSearch || undefined,
    include_completed: showCompleted || undefined,
    limit: 500,
  })
  const { data: vocabResp } = useTrainingVocabularies()
  const update = useUpdateTrainingWorksheetRow(sheet)

  const rows = data?.data ?? []
  const canEdit = ['Super Admin', 'Admin', 'User'].includes(userType || '')

  // The server decides whether this tab has a notion of "completed" at all — Office
  // Hours is an attendance log and has none — so the control appears only where it
  // means something, rather than this component hardcoding five of the six.
  const hasCompletedFilter = Boolean(data?.stats?.hasCompletedFilter)
  const completedHidden = Number(data?.stats?.completedHidden ?? 0)

  // Rebuilt on every render otherwise, and Onboarding's is 25 descriptors including
  // four flatMap groups.
  const resolvedColumns = useMemo(() => columns(vocabResp?.data), [columns, vocabResp])

  const save = async (row: TrainingWorksheetRow, key: string, value: unknown) => {
    try {
      await update.mutateAsync({ id: row.id, data: { [key]: value } })
    } catch (err) {
      toast({
        title: 'Could not save that change',
        description: explainError(err, 'Please try again.'),
        variant: 'destructive',
      })
    }
  }

  return (
    <>
      <SheetPage
        title={title}
        description={description}
        icon={icon}
        toolbar={
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[14rem] flex-1">
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-9"
                aria-label={`Search ${title}`}
              />
            </div>
            {hasCompletedFilter && (
              <ShowCompletedToggle
                showCompleted={showCompleted}
                onToggle={() => setShowCompleted((v) => !v)}
              />
            )}
            <span className="text-xs text-muted-foreground">
              {rows.length} of {data?.total ?? rows.length} shown
            </span>
            <CompletedHiddenNote hiddenCount={completedHidden} />
          </div>
        }
      >
        <SheetTable<TrainingWorksheetRow>
          rows={rows}
          columns={resolvedColumns}
          rowId={(r) => r.id}
          tint={tintFor}
          rowLabel={rowLabel}
          canEdit={() => canEdit}
          onSave={save}
          isLoading={isLoading}
          emptyMessage={
            // The filter is the most likely reason this list is empty, so it is checked
            // before "there is nothing here" — on Advanced Demo it is the normal case.
            completedHidden > 0 && !debouncedSearch ? (
              <NothingOutstanding
                hiddenCount={completedHidden}
                onShow={() => setShowCompleted(true)}
              />
            ) : (
              (emptyMessage ??
              (debouncedSearch
                ? 'Nothing matches that search.'
                : `No ${title} rows yet. They arrive with the tracker import.`))
            )
          }
          caption={caption}
        />
      </SheetPage>

      <div className="space-y-3 px-4 pb-6 md:px-6">
        {tints && <SheetColourKey tints={tints} />}
        {/*
          One queue for the whole tracker, not one per tab. core_crm.account_aliases
          is unique on lower(alias) across all sources, so a name unmatched on two
          tabs is queued once — filtering per tab would hide it on whichever tab did
          not happen to record it first. Resolving it here fixes every tab.
        */}
        {canEdit && <AccountAliasResolver source="training-tracker" />}
      </div>
    </>
  )
}
